import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavigationProps {
  transparent?: boolean;
}

export default function Navigation({ transparent = false }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Services', path: '/services' },
    { name: 'Turnkey Solutions', path: '/turnkey-solutions' },
    { name: 'Who We Serve', path: '/who-we-serve' },
    { name: 'Why Mediline', path: '/why-mediline' },
    { name: 'Coverage Areas', path: '/coverage-areas' },
    { name: 'Careers', path: '/careers' },
  ];

  const isTransparent = transparent && !isScrolled;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isTransparent
            ? 'bg-navy/85 backdrop-blur-sm'
            : 'bg-white shadow-md'
        } ${isLoaded ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img
                src="https://public.readdy.ai/ai/img_res/41df48de-aa08-43cf-8e09-06eb0d30994f.png"
                alt="Mediline Services"
                className="h-9 sm:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Center Navigation - Desktop */}
            <div className="hidden lg:flex items-center space-x-10">
              {navLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[15px] font-medium tracking-wide transition-all duration-300 hover:opacity-100 whitespace-nowrap relative group ${
                    isTransparent ? 'text-white' : 'text-navy'
                  } ${location.pathname === link.path ? 'opacity-100' : 'opacity-70'}`}
                  style={{ 
                    transitionDelay: isLoaded ? `${index * 50}ms` : '0ms',
                    opacity: isLoaded ? undefined : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(-10px)'
                  }}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-current transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              ))}
            </div>

            {/* Request Quote Button - Desktop */}
            <Link
              to="/contact"
              className={`hidden lg:block px-8 py-3 rounded-full border-2 transition-all duration-300 font-medium text-[15px] whitespace-nowrap cursor-pointer hover:scale-105 ${
                isTransparent
                  ? 'border-white text-white hover:bg-white hover:text-navy'
                  : 'border-navy text-navy hover:bg-navy hover:text-white'
              }`}
            >
              Request Quote
            </Link>

            <Link
              to="/service-request"
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                isScrolled || !transparent
                  ? 'bg-navy-dark text-white hover:bg-navy-dark/90'
                  : 'bg-white text-navy-dark hover:bg-white/90'
              }`}
            >
              Request Service
            </Link>

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors duration-300 cursor-pointer ${isTransparent ? 'text-white' : 'text-navy'}`}
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 lg:hidden ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[280px] bg-white z-[70] shadow-2xl transition-transform duration-300 ease-out lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <span className="text-navy font-semibold text-lg">Menu</span>
          <button
            className="w-10 h-10 flex items-center justify-center text-navy cursor-pointer"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        <div className="flex flex-col p-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`text-[15px] font-medium py-3.5 border-b border-gray-50 transition-colors duration-300 ${
                location.pathname === link.path ? 'text-slate-blue' : 'text-navy'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className="mt-6 px-6 py-3.5 rounded-full border-2 border-navy text-navy text-center font-medium text-[15px] whitespace-nowrap transition-all duration-300 hover:bg-navy hover:text-white cursor-pointer"
          >
            Request Quote
          </Link>
        </div>
      </div>
    </>
  );
}
